import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Field, FieldArray, fieldArrayPropTypes } from 'redux-form';
import PropTypes from 'prop-types';
import Dragula from 'react-dragula';
import 'react-dragula/dist/dragula.css';
import { v4 as uuidv4 } from 'uuid';
import Icon from 'common/components/fa/Icon';
import { labelToKey, OTHER_CONTACT_ORIGIN_KEY } from 'common/utils/contactOrigin';

// Input contrôlé par redux-form pour le label d'une origine.
// Le blur déclenche `onBlurNormalize` qui recalcule le label capitalisé et la key associée.
const LabelField = ({ input, meta, disabled, onBlurNormalize }) => {
    const hasError = meta.touched && meta.error;

    return (
        <Fragment>
            <input
                type="text"
                className={hasError ? 'contact-origin-list__input contact-origin-list__input--error' : 'contact-origin-list__input'}
                value={input.value || ''}
                disabled={disabled}
                onChange={input.onChange}
                onBlur={() => onBlurNormalize(input.value || '')}
            />
            {hasError ? (
                <div className="contact-origin-list__item-error">{meta.error}</div>
            ) : null}
        </Fragment>
    );
};

LabelField.propTypes = {
    input: PropTypes.shape({
        value: PropTypes.string,
        onChange: PropTypes.func.isRequired,
    }).isRequired,
    meta: PropTypes.shape({
        touched: PropTypes.bool,
        error: PropTypes.string,
    }).isRequired,
    disabled: PropTypes.bool,
    onBlurNormalize: PropTypes.func.isRequired,
};

LabelField.defaultProps = {
    disabled: false,
};

const ContactOriginListInner = ({ fields, meta }) => {
    const containerRef = useRef(null);
    // Ref sur `fields` pour y accéder dans le handler Dragula sans recréer l'instance à chaque render
    const fieldsRef = useRef(fields);
    const [newLabel, setNewLabel] = useState('');
    const [createError, setCreateError] = useState(null);

    fieldsRef.current = fields;

    useEffect(() => {
        // Initialisation Dragula : drag autorisé uniquement via la poignée (icône "bars")
        const drake = Dragula([containerRef.current], {
            moves: (el, source, handle) => handle.classList.contains('contact-origin-list__handle')
                || !!handle.closest('.contact-origin-list__handle'),
        });

        drake.on('drop', (el, target, source, sibling) => {
            const currentFields = fieldsRef.current;
            const fromIndex = Number(el.getAttribute('data-index'));
            // Si pas de sibling (dépôt en fin de liste), l'index cible est la longueur totale
            const siblingIndex = sibling ? Number(sibling.getAttribute('data-index')) : currentFields.length;

            drake.cancel(true);

            if (Number.isNaN(fromIndex)) {
                return;
            }

            // Décalage à compenser quand on descend l'élément
            const toIndex = fromIndex < siblingIndex ? siblingIndex - 1 : siblingIndex;

            if (fromIndex !== toIndex) {
                currentFields.move(fromIndex, toIndex);
            }
        });

        return () => drake.destroy();
    }, []);

    const handleAdd = () => {
        const trimmed = newLabel.trim();

        if ('' === trimmed) {
            setCreateError('Veuillez saisir une origine');

            return;
        }

        const exists = fields.getAll().some(({ label }) => (label || '').trim().toLowerCase() === trimmed.toLowerCase());

        if (exists) {
            setCreateError('Cette origine existe déjà');

            return;
        }

        const id = uuidv4();
        const capitalized = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);

        fields.push({ id, key: labelToKey(capitalized), label: capitalized });
        setNewLabel('');
        setCreateError(null);
    };

    const handleRemove = (index, entry) => {
        if (entry.key === OTHER_CONTACT_ORIGIN_KEY) {
            return;
        }

        fields.remove(index);
    };

    // Au blur d'un input : trim + capitalisation du label et resync de la key (slug technique)
    const handleLabelBlur = (index, rawValue) => {
        const currentFields = fieldsRef.current;
        const entry = currentFields.get(index) || {};

        if (entry.key === OTHER_CONTACT_ORIGIN_KEY) {
            return;
        }

        const trimmed = (rawValue || '').trim();
        const capitalized = '' === trimmed
            ? ''
            : trimmed.charAt(0).toUpperCase() + trimmed.slice(1);

        currentFields.splice(index, 1, {
            ...entry,
            label: capitalized,
            key: labelToKey(capitalized),
        });
    };

    return (
        <Fragment>
            <ul ref={containerRef} className="contact-origin-list">
                {fields.map((name, index) => {
                    const entry = fields.get(index) || {};
                    const isOther = entry.key === OTHER_CONTACT_ORIGIN_KEY;

                    return (
                        <li
                            key={entry.id || name}
                            data-index={index}
                            className="contact-origin-list__item"
                        >
                            <span className="contact-origin-list__handle" title="Glisser pour réordonner">
                                <Icon icon="bars" />
                            </span>
                            <div className="contact-origin-list__label">
                                <Field
                                    name={`${name}.label`}
                                    component={LabelField}
                                    disabled={isOther}
                                    onBlurNormalize={(rawValue) => handleLabelBlur(index, rawValue)}
                                />
                            </div>
                            <button
                                type="button"
                                className={isOther ? 'bouton contact-origin-list__action contact-origin-list__delete--locked' : 'bouton contact-origin-list__action'}
                                onClick={() => handleRemove(index, entry)}
                                disabled={isOther}
                                title={isOther ? 'Origine non supprimable' : 'Supprimer'}
                            >
                                <Icon icon="trash-o" />
                            </button>
                        </li>
                    );
                })}
            </ul>
            {meta && meta.error ? (
                <div className="contact-origin-list__error">{meta.error}</div>
            ) : null}
            <div className="contact-origin-list__create">
                <input
                    type="text"
                    className={createError ? 'contact-origin-list__input contact-origin-list__input--error' : 'contact-origin-list__input'}
                    placeholder="Nouvelle origine"
                    value={newLabel}
                    onChange={(e) => {
                        setNewLabel(e.target.value);

                        if (createError) {
                            setCreateError(null);
                        }
                    }}
                    onKeyDown={(e) => {
                        if ('Enter' === e.key) {
                            e.preventDefault();
                            handleAdd();
                        }
                    }}
                />
                <button
                    type="button"
                    className="bouton contact-origin-list__add"
                    onClick={handleAdd}
                >
                    Ajouter
                </button>
            </div>
            {createError ? (
                <div className="contact-origin-list__item-error">{createError}</div>
            ) : null}
        </Fragment>
    );
};

ContactOriginListInner.propTypes = {
    ...fieldArrayPropTypes,
};

const ContactOriginList = () => (
    <FieldArray name="global.contactOriginTypes" component={ContactOriginListInner} />
);

export default ContactOriginList;
